import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Lock, Phone, ShieldCheck, MessageCircle, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient, formatApiError } from "@/lib/api";
import { imgUrl } from "@/utils/format";
import { roleHome } from "@/utils/roles";
import { COUNTRIES, buildE164 } from "@/components/PhoneInput";
import AppleLoginButton from "@/components/AppleLoginButton";
import { isNativeApp } from "@/utils/platform";
import { startGoogleLogin, startNativeGoogleLogin } from "@/utils/googleOauth";
import { toast } from "sonner";

// Support NelDaari (WhatsApp) — réinitialisation de mot de passe
const SUPPORT_WHATSAPP = "33758262447";

export default function Login() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const { login, refreshUser, user, loading: authLoading } = useAuth();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [phoneCode, setPhoneCode] = useState("224");
  const [forgot, setForgot] = useState(false);
  const [wrongSpace, setWrongSpace] = useState(""); // message "compte pro"
  // MFA challenge after login
  const [mfa, setMfa] = useState(null); // { challenge_id, phone_hint }
  const [mfaCode, setMfaCode] = useState("");
  const navigate = useNavigate();

  // Utilisateur déjà connecté qui arrive sur /login -> redirection automatique
  useEffect(() => {
    if (!authLoading && user && !mfa) {
      navigate(next && next !== "/" ? next : roleHome(user.role), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const goAfterLogin = async (userData) => {
    if (next && next !== "/") { navigate(next); return; }
    let role = userData?.role;
    if (!role) {
      try { role = (await apiClient.get("/auth/me")).data?.role; } catch { role = null; }
    }
    navigate(roleHome(role));
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Client OAuth Google « NelDaari » — code échangé côté backend (voir utils/googleOauth.js)
  const handleGoogle = () => {
    if (isNativeApp) { startNativeGoogleLogin(); return; }
    startGoogleLogin();
  };

  // Identifiant effectif : email tel quel, "+..."/"00..." tel quel,
  // sinon numéro national combiné à l'indicatif pays choisi
  const isEmailMode = form.identifier.includes("@") || /[a-zA-Z]/.test(form.identifier);
  const effectiveIdentifier = () => {
    const raw = form.identifier.trim();
    if (!raw || isEmailMode || raw.startsWith("+") || raw.startsWith("00")) return raw;
    return buildE164(phoneCode, raw);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setWrongSpace("");
    try {
      const data = await login(effectiveIdentifier(), form.password, "client");
      if (data?.mfa_required) {
        setMfa({ challenge_id: data.challenge_id, phone_hint: data.phone_hint });
        try {
          await apiClient.post("/auth/mfa/send", { challenge_id: data.challenge_id });
          toast.success(t("auth.mfaSent", { phone: data.phone_hint }));
        } catch (err) { toast.error(formatApiError(err)); }
      } else {
        toast.success(t("auth.loginSuccess"));
        await goAfterLogin(data);
      }
    } catch (err) {
      const msg = formatApiError(err);
      if (err?.response?.status === 403) setWrongSpace(msg);
      else toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post("/auth/mfa/verify", { challenge_id: mfa.challenge_id, code: mfaCode });
      await refreshUser();
      toast.success(t("auth.mfaVerified"));
      await goAfterLogin(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={imgUrl("/api/static/images/dish_riz_gras.png")} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-xs uppercase tracking-[0.25em] mb-2 text-[#F4C430]">NelDaari</div>
          <h2 className="font-display text-4xl font-semibold leading-tight">{t("auth.heroLine1")}<br/>{t("auth.heroLine2")}</h2>
        </div>
      </div>
      <div className="grid place-items-center px-4 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="font-display text-2xl font-semibold inline-flex items-center gap-2 mb-6">
            <img src="/logo-neldaari.png" alt="NelDaari" className="w-8 h-8" /> NelDaari
          </Link>

          {mfa ? (
            <>
              <h1 className="font-display text-3xl font-semibold flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-brand-green" /> {t("auth.mfaTitle")}</h1>
              <p className="text-mute mb-6">{t("auth.mfaHint", { phone: mfa.phone_hint })}</p>
              <form onSubmit={submitMfa} className="space-y-3">
                <input
                  data-testid="mfa-code"
                  type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))}
                  required
                  placeholder="123456"
                  className="w-full bg-elevated rounded-xl py-4 px-4 text-center text-2xl tracking-[0.5em] outline-none font-display"
                  autoCapitalize="off" autoCorrect="off" spellCheck="false"
                />
                <button type="submit" data-testid="mfa-submit" disabled={loading || mfaCode.length < 4} className="w-full rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white py-3 font-semibold">
                  {loading ? t("auth.verifying") : t("auth.verify")}
                </button>
                <button type="button" onClick={async () => { try { await apiClient.post("/auth/mfa/send", { challenge_id: mfa.challenge_id }); toast.success(t("auth.codeResent")); } catch (err) { toast.error(formatApiError(err)); } }} className="w-full text-xs text-mute hover:text-brand-red">
                  {t("auth.resendCode")}
                </button>
              </form>
            </>
          ) : forgot ? (
            <>
              <h1 className="font-display text-3xl font-semibold">{t("auth.forgotTitle")}</h1>
              <p className="text-mute mb-6">{t("auth.forgotHint")}</p>
              <a
                href={`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(
                  `Bonjour, j'ai oublié mon mot de passe. Mon compte est le ${effectiveIdentifier() || "+224..."}`
                )}`}
                target="_blank"
                rel="noreferrer"
                data-testid="forgot-whatsapp-btn"
                className="w-full rounded-full bg-brand-green hover:bg-[#007A4A] text-white py-3 font-semibold inline-flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-5 h-5" /> {t("auth.contactWhatsapp")}
              </a>
              <button type="button" onClick={() => setForgot(false)} data-testid="forgot-back" className="mt-4 w-full text-sm text-mute hover:text-ink inline-flex items-center justify-center gap-1">
                <ArrowLeft className="w-4 h-4" /> {t("auth.backToLogin")}
              </button>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl font-semibold">{t("auth.loginTitle")} 👋</h1>
              <p className="text-mute mb-6">{t("auth.loginSubtitle")}</p>

              {wrongSpace && (
                <div className="mb-4 rounded-xl border border-brand-red/30 bg-[#FFF5F6] p-3 text-sm" data-testid="wrong-space-alert">
                  {wrongSpace}
                </div>
              )}

              <button onClick={handleGoogle} data-testid="login-google-btn" className="w-full mb-3 rounded-full border border-sand bg-white hover:bg-elevated py-3 font-medium inline-flex items-center justify-center gap-2 text-sm">
                <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.5 5.7 28.9 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C33.5 6.7 28.9 5 24 5 16.3 5 9.7 9.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c4.8 0 9.2-1.6 12.6-4.4l-5.8-4.9C28.7 36 26.5 37 24 37c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.5 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l5.8 4.9C40.2 35.4 44 30.1 44 24c0-1.2-.1-2.4-.4-3.5z"/></svg>
                {t("auth.continueGoogle")}
              </button>
              <AppleLoginButton label={t("auth.continueApple")} />
              <div className="text-center text-xs uppercase tracking-widest text-mute my-2">{t("common.or")}</div>

              <form onSubmit={submit} className="space-y-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("auth.phoneOrEmail")}</label>
                  <div className="flex items-stretch gap-2">
                    {!isEmailMode && (
                      <div className="relative shrink-0">
                        <select
                          value={phoneCode}
                          onChange={(e) => setPhoneCode(e.target.value)}
                          data-testid="login-country-code"
                          aria-label="Indicatif pays"
                          className="h-full appearance-none bg-elevated rounded-xl pl-2.5 pr-6 py-3 text-sm outline-none cursor-pointer"
                        >
                          {COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>{c.flag} +{c.code}</option>
                          ))}
                        </select>
                        <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-mute">▼</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 bg-elevated rounded-xl px-3 flex-1 min-w-0">
                      <Phone className="w-4 h-4 text-mute shrink-0" />
                      <input data-testid="login-email" type="text" required value={form.identifier} onChange={(e) => setForm({...form, identifier: e.target.value})} className="w-full bg-transparent py-3 outline-none" placeholder={t("auth.identifierPh")} autoComplete="username" autoCapitalize="off" autoCorrect="off" spellCheck="false" />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-mute mb-1 block">{t("auth.passwordLabel")}</label>
                  <div className="flex items-center gap-2 bg-elevated rounded-xl px-3">
                    <Lock className="w-4 h-4 text-mute" />
                    <input data-testid="login-password" type={show ? "text" : "password"} required value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="w-full bg-transparent py-3 outline-none" placeholder="••••••••" autoComplete="current-password" autoCapitalize="off" autoCorrect="off" spellCheck="false" />
                    <button type="button" onClick={() => setShow(!show)} className="text-mute">{show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                  </div>
                </div>
                <div className="text-right">
                  <button type="button" onClick={() => setForgot(true)} data-testid="forgot-password-link" className="text-xs text-mute hover:text-brand-red">{t("auth.forgotLink")}</button>
                </div>
                <button type="submit" data-testid="login-submit" disabled={loading} className="w-full rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-60 text-white py-3 font-semibold">
                  {loading ? t("common.loading") : t("auth.loginBtn")}
                </button>
              </form>
            </>
          )}

          {!mfa && !forgot && (
            <div className="mt-6 text-center space-y-3">
              <p className="text-sm text-mute">
                {t("auth.noAccount")} <Link to="/register" data-testid="goto-register" className="text-brand-red font-semibold">{t("auth.createAccount")}</Link>
              </p>
              <Link
                to="/espace-partenaires"
                data-testid="login-partner-link"
                className="flex items-center justify-center gap-2 rounded-full border border-brand-red/30 bg-[#FFF5F6] hover:bg-brand-red hover:text-white text-brand-red px-4 py-2.5 text-sm font-semibold transition-colors"
              >
                {t("auth.partnerLoginCta")} <span aria-hidden>→</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
